import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { RankedPromoOfferDiscount } from 'src/apollo/onlineOrdering';
import useTracker from 'src/lib/js/hooks/useTracker';
import { useCart } from 'src/public/components/online_ordering/CartContext';
import { useCheckout } from 'src/public/components/online_ordering/CheckoutContext';
import { useGiftCard } from 'src/public/components/online_ordering/GiftCardContext';
import { useOffers } from 'src/public/components/online_ordering/OffersContext';
import ToastProduct from 'src/public/components/online_ordering/ToastProduct';
import { Modal, ModalContent, ModalOverlay, useModal } from 'src/shared/components/common/modal';
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext';
import { alertSuccess, alertError } from 'src/shared/js/alertUtils';

import config from 'config';

import DiscountPill, { DiscountPillState, DiscountPillType } from './DiscountPill';
import DiscountsModal, { DiscountsModalType } from './DiscountsModal';

const OffersSection = () => {
  const { achievedOffers } = useOffers();
  const { cart, applyPromoCode, removePromoCode, cartGuid, refetchCart } = useCart();
  const { isOpen, onClose, onOpen } = useModal();

  const currentAppliedOffer = useMemo(() =>
    cart?.order?.discounts?.restaurantDiscount?.promoCode?.toUpperCase(),
  [cart?.order?.discounts?.restaurantDiscount?.promoCode]);

  const applyOffer = useCallback(async (offer: string) => {
    const applyError = await applyPromoCode(offer);
    if(applyError) {
      alertError('Error applying promo code');
    } else {
      alertSuccess('Offer successfully applied!');
      onClose();
    }
  }, [applyPromoCode, onClose]);

  const removeOffer = useCallback(async () => {
    const removeError = await removePromoCode();
    if(removeError) {
      alertError('Error removing promo code');
    }
  }, [removePromoCode]);

  const onClickOffer = useCallback(async (offer: RankedPromoOfferDiscount) => {
    if(currentAppliedOffer === offer.promoCode) {
      await removeOffer();
    } else {
      await applyOffer(offer.promoCode);
    }
  }, [applyOffer, currentAppliedOffer, removeOffer]);

  // Gift Cards
  const { giftCardNumber, applyGiftCard, applyGiftCardExtension, giftCardError, removeGiftCard } = useGiftCard();
  const tracker = useTracker();
  const { ooRestaurant, toastProduct } = useRestaurant();
  const { giftCardAppliedAmount } = useCheckout();
  const gcLast4 = useMemo(() => giftCardNumber.slice(-4), [giftCardNumber]);
  const gcConfig = ooRestaurant?.giftCardConfig;
  const prevGiftCardAppliedAmount:React.MutableRefObject<number> = useRef(0);
  const firstOrThirdParty = gcConfig?.hasGiftCardsExtension ? '3P' : '1P';
  const acceptsGiftCards = useMemo(() => {
    const accepts3PGC = gcConfig?.hasGiftCardsExtension && ![ToastProduct.OOBasic, ToastProduct.ToastLocal].includes(toastProduct);
    return gcConfig?.redemptionAllowed || accepts3PGC;
  },
  [gcConfig?.hasGiftCardsExtension, gcConfig?.redemptionAllowed, toastProduct]);

  const applyGiftCardToCart = useCallback(async (giftCardNumber: string, code?: string) => {
    if(cartGuid && giftCardNumber && acceptsGiftCards) {
      if(gcConfig?.hasGiftCardsExtension) {
        tracker.track('Clicked apply gift card', {
          cartSource: cart?.cartSource,
          firstOrThirdParty,
          verificationCodeSubmitted: Boolean(code)
        });
        grecaptcha.enterprise.ready(async () => {
          const token = await grecaptcha.enterprise.execute(config.reCaptchaEnterprise.siteKey, { action: 'giftcard_extension_inquiry' });
          const tokenProto = `recaptcha:enterprise:${token}`;
          await applyGiftCardExtension(cartGuid, giftCardNumber, tokenProto, code);
        });
      } else {
        tracker.track('Clicked apply gift card', {
          cartSource: cart?.cartSource,
          firstOrThirdParty,
          verificationCodeSubmitted: false
        });
        applyGiftCard(cartGuid, giftCardNumber);
      }
    }
  }, [acceptsGiftCards, applyGiftCard, applyGiftCardExtension, cart, cartGuid, firstOrThirdParty, gcConfig?.hasGiftCardsExtension, tracker]);

  const removeGiftCardFromCart = useCallback(() => {
    removeGiftCard();
  }, [removeGiftCard]);

  useEffect(() => {
    if(prevGiftCardAppliedAmount.current != giftCardAppliedAmount) {
      prevGiftCardAppliedAmount.current = giftCardAppliedAmount;
      refetchCart({ totalGiftCardBalance: giftCardAppliedAmount });
    }
  }, [refetchCart, giftCardAppliedAmount]);

  useEffect(() => {
    if(giftCardNumber) {
      alertSuccess('Gift card applied successfully');
      tracker.track('Gift card applied successfully', {
        cartSource: cart?.cartSource,
        firstOrThirdParty
      });
      setModalCurrentValue(undefined);
      onClose();
    } else if(giftCardError) {
      alertError('Error applying gift card');
      tracker.track('Gift card error', {
        cartSource: cart?.cartSource,
        firstOrThirdParty,
        error: giftCardError
      });
    }
  }, [cart, giftCardNumber, giftCardError, tracker, firstOrThirdParty, onClose]);
  const [gcWarningModalOpen, setGCWarningModalOpen] = useState(false);

  const promoModifierText = useMemo(() => `${currentAppliedOffer ? 'Update' : 'Add'} Promo Code`, [currentAppliedOffer]);
  const gcModifierText = useMemo(() => `${giftCardNumber.length !== 0 ? 'Update' : 'Add'} Gift Card`, [giftCardNumber]);
  const [modalType, setModalType] = useState<DiscountsModalType>(DiscountsModalType.OFFER);
  const [modalCurrentValue, setModalCurrentValue] = useState<string | undefined>(currentAppliedOffer);
  const [modalHeader, setModalHeader] = useState<string>('');
  const [modalDescription, setModalDescription] = useState<string | undefined>(undefined);
  const onApply = useCallback(async (primary: string, secondary: string) => {
    if(modalType === DiscountsModalType.OFFER) {
      await applyOffer(primary);
    } else {
      await applyGiftCardToCart(primary, secondary);
    }
  }, [applyGiftCardToCart, applyOffer, modalType]);

  return (
    <div className="offersSectionContainer">
      <div className="header">
        Offers & Gifts (1 of each per order)
      </div>
      <div className="offerPills">
        {currentAppliedOffer && !achievedOffers?.find(offer => offer.promoCode === currentAppliedOffer) ?
          <DiscountPill
            pillKey="discount-promo-pill"
            text={currentAppliedOffer}
            state={DiscountPillState.ACTIVE}
            onClick={removeOffer} />
          : null}
        {giftCardNumber.length !== 0 &&
        <DiscountPill
          pillKey="discount-gc-pill"
          text={`Gift Card ${gcLast4}`}
          state={DiscountPillState.ACTIVE}
          onClick={() => setGCWarningModalOpen(true)} />}
        {achievedOffers?.map(offer =>
          <DiscountPill
            key={`discount-offer-pill-${offer.bannerGuid}`}
            text={offer.bannerText}
            state={getDiscountPillState(offer, currentAppliedOffer)}
            onClick={() => onClickOffer(offer)} />)}
      </div>
      <div className="buttonContainer">
        <DiscountPill
          text={`${promoModifierText} +`}
          type={DiscountPillType.BUTTON}
          onClick={() => {
            onOpen();
            setModalType(DiscountsModalType.OFFER);
            setModalHeader('Promo Code');
            setModalDescription('Any offers your cart qualifies for will appear at checkout. Use this section to apply any unlisted promo codes.');
            setModalCurrentValue(currentAppliedOffer);
          }}
          testId="add-promo-modal-btn" />
        {acceptsGiftCards && <DiscountPill
          text={`${gcModifierText} +`}
          type={DiscountPillType.BUTTON}
          onClick={() => {
            onOpen();
            setModalHeader('Gift Card');
            setModalDescription(undefined);
            setModalType(DiscountsModalType.GIFT_CARD);
            setModalCurrentValue(giftCardNumber);
          }}
          testId="add-gc-modal-btn" />}
        <DiscountsModal
          header={modalHeader}
          description={modalDescription}
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={onClose}
          onApply={onApply}
          currentValue={modalCurrentValue}
          type={modalType} />
        <RemoveGCWarningModal isOpen={gcWarningModalOpen} setIsOpen={setGCWarningModalOpen} onConfirm={removeGiftCardFromCart} />
      </div>
    </div>
  );
};

const getDiscountPillState = (offer: RankedPromoOfferDiscount, currentAppliedDiscount: string | undefined) => {
  if(offer.percentCompleteInt < 100) {
    return DiscountPillState.LOCKED;
  }
  if(currentAppliedDiscount === offer.promoCode.toUpperCase()) {
    return DiscountPillState.ACTIVE;
  }
  return DiscountPillState.INACTIVE;
};

interface RemoveGCWarningModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onConfirm: () => void;
}
const RemoveGCWarningModal = ({ isOpen, setIsOpen, onConfirm }: RemoveGCWarningModalProps) => {
  const onClose = () => setIsOpen(false);
  return (
    <Modal isOpen={isOpen} onClose={onClose} testId="remove-gc-modal">
      <ModalOverlay />
      <ModalContent>
        <div className="discountsModal">
          <div className="header">Remove gift card?</div>
          <div className="description">If you wish to re-add this gift card later, you will have to type it in again.</div>
          <div className="buttonContainer">
            <button className="modalButton cancel" type="button" onClick={onClose}>Cancel</button>
            <button className="modalButton confirm"
              type="button"
              onClick={() => {
                onConfirm();
                onClose();
              }}>Yes, remove
            </button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};


export default OffersSection;
